import { Accordion, Stack, Text } from '@ltvco/refresh-lib/theme';
import React from 'react';

import { ErrorStateContainer, EmptyStateContainer } from '../components';
import { InlineLoading } from '../LoadingState';
import { ResultsGrid } from '../ResultsGrid';
import { ViewToggle, type ViewType } from '../ViewToggle';

export interface AccordionResultsSectionProps<T> {
  title: string;
  isLoading?: boolean;
  isError?: boolean;
  error?: unknown;
  errorLabel?: string;
  results: T[];
  emptyMessage: React.ReactNode;
  renderCard: (item: T, index: number) => React.ReactNode;
  getKey: (item: T, index: number) => string | number;
  defaultExpanded?: boolean;
}

export function AccordionResultsSection<T>({
  title,
  isLoading = false,
  isError = false,
  error,
  errorLabel = 'Error loading content',
  results,
  emptyMessage,
  renderCard,
  getKey,
  defaultExpanded = true,
}: AccordionResultsSectionProps<T>): React.ReactElement {
  const [viewType, setViewType] = React.useState<ViewType>('grid');

  return (
    <Accordion display={title} defaultExpanded={defaultExpanded}>
      {isLoading ? (
        <InlineLoading />
      ) : isError ? (
        <ErrorStateContainer>
          <Text color="error">
            {errorLabel}:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </Text>
        </ErrorStateContainer>
      ) : !results.length ? (
        <EmptyStateContainer>
          <Text variant="body1" color="text.secondary">
            {emptyMessage}
          </Text>
        </EmptyStateContainer>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            <ViewToggle
              currentView={viewType}
              onViewChange={(view) => setViewType(view)}
            />
          </Stack>
          <ResultsGrid
            results={results}
            viewType={viewType}
            renderCard={renderCard}
            getKey={getKey}
          />
        </Stack>
      )}
    </Accordion>
  );
}
